import React, { ReactElement, useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { Editor, IAllProps } from './reactTinymce';
import { editorConfig } from './editorConfig';
// import { TinyMCE as EditorType } from './tinymce.d';
import * as EditorTypeAll from './tinymce.d';
import Theme, { ToolbarAlwaysType } from './theme/Theme.tsx';
import { LinkManagerProvider, useLinkManager } from './LinkManager';
import { createLinkDialogHandler, LinkDialogHandler } from './LinkDialogHandler';
import { useEditorDragDropWithListeners } from './useEditorDragDrop';
import quotedDot from '@/components/editor/theme/image/quoted-dot.svg';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@leadsnavi/ui-kit';
import { useTranslation } from 'react-i18next';
// import AITextInput from '../ai-writer/ai-input/index.tsx';
// import AICard from '../ai-writer/ai-card/index.tsx';

type EditorType = EditorTypeAll.Editor;

declare type Props = IAllProps & {
  uniqueClass?: string;
  height?: number;
  toolbarAlways: ToolbarAlwaysType;
  toolbarExtendA?: ToolbarAlwaysType;
  toolbarExtend?: ToolbarAlwaysType;
  toolbarAutoExtend?: ToolbarAlwaysType;
  toolbarAutoExtendRow2?: ToolbarAlwaysType;
  children?: ReactElement;
  onEditCreated?: (e: EditorType) => void;
  grammarAction?: (e: EditorType) => void;
  previewAction?: (e: EditorType) => void;
  mouseoverToolbar?: (e: EditorType) => void;
  mouseoutToolbar?: (e: EditorType) => void;
  captureScreenAction?: (e: EditorType) => void;
  productImgAction?: (e: EditorType) => void;
  utilsAction?: (e: EditorType) => void;
  colorAction?: (e: EditorType) => void;
  fontSizeAction?: (e: EditorType) => void;
  lineheightAction?: (e: EditorType) => void;
  fontfamilyAction?: (e: EditorType) => void;
  insertEmojiAction?: (emojiIconPosition: DOMRect, callback: (emoji: string) => object) => void;
  aiWriteMailAction?: (type: 'write' | 'retouch') => void;
  AIWriteContentionShowAction?: React.Dispatch<boolean>;
  insertContentProductImgCallback?: (hasNewCreated: boolean) => void;
  enableContentHeightTracking?: boolean;
  onContentHeightChange?: (height: number) => void;
  customToolbarPlugin?: React.ReactNode;
  quotedContentAction?: () => void;
  showQuotedDot?: boolean;
  enableDraggingAttachmentUpload?: boolean;
  enableDraggingImageUpload?: boolean;
};

const siriusVersion = '1.4';
const eventsOut = (ed: EditorType) => {
  // 将编辑器的事件冒泡到主页面 qwe
  // const eventsMap = {
  //   MouseEvents: ['mousedown', 'click'],
  // };
  ['mousedown', 'click'].forEach(eventName => {
    ed.on(eventName, () => {
      if (document) {
        const event = document.createEvent('MouseEvents');
        event.initEvent(eventName, true, false);
        document.body.dispatchEvent(event);
      }
    });
  });
};

// 内部编辑器组件，使用LinkManager
const LxEditorInner = React.forwardRef(
  ({
    init = {},
    toolbarAlways,
    toolbarExtendA = [],
    toolbarExtend = [],
    toolbarAutoExtend = [],
    toolbarAutoExtendRow2 = [],
    uniqueClass,
    grammarAction,
    height,
    children,
    insertEmojiAction,
    onEditCreated,
    aiWriteMailAction,
    AIWriteContentionShowAction,
    mouseoverToolbar,
    mouseoutToolbar,
    captureScreenAction,
    previewAction,
    productImgAction,
    utilsAction,
    colorAction,
    fontSizeAction,
    lineheightAction,
    fontfamilyAction,
    enableContentHeightTracking = false,
    onContentHeightChange,
    customToolbarPlugin,
    quotedContentAction,
    showQuotedDot = false,
    enableDraggingAttachmentUpload = true,
    enableDraggingImageUpload = true,
    ...rest
  }: Props) => {
    const { openLinkDialog } = useLinkManager();
    const [editorInstance, seteditorInstance] = useState<EditorType>();
    // const [showLayer, setShowLayer] = useState(false);
    const [linkDialogHandler, setLinkDialogHandler] = useState<LinkDialogHandler | null>(null);
    const [bottomContainerHeight, setBottomContainerHeight] = useState(48); // 默认48px
    const [editorContentHeight, setEditorContentHeight] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    // const containerContentRef = useRef<HTMLDivElement>(null);
    const bottomContainerRef = useRef<HTMLDivElement>(null);
    // 扩大的拖拽捕获容器引用，用于扩大拖拽捕获区域
    // const dragCaptureRef = useRef<HTMLDivElement>(null);
    // const dragCounterRef = useRef(0);
    // 为每个编辑器实例生成唯一的 ID，确保多个编辑器实例不会互相干扰（特别是右键菜单）
    const editorIdRef = useRef<string>(`editor-${uniqueClass || 'default'}-${Date.now()}`);
    const editorId = editorIdRef.current;
    // redux editor_ai_mode value
    const orgToolbar =
      init.toolbar ||
      `undo redo lxformatpainter removeformat $split fontselect fontsizeselect
  bold italic underline forecolor backcolor $split bullist numlist $split alignleftSplit
  dentSplit lxinputdirection $split lxTable lximg link lxemoji $split print code`;
    const themeParam = {
      attachment_upload_handler: init.attachment_upload_handler,
      className: init.className,
      style: init.style,
      allowedFileTypes: init.allowedFileTypes,
    };
    // const debounceDragout = debounce(function () {
    // setShowLayer(false);
    // }, 500);

    // 高度模式判断逻辑（优先级：height > editor_max_height > 自适应）
    const heightMode = (() => {
      if (init.height) return 'fixed'; // 固定高度模式：使用传入的height值
      if (init.editor_max_height) return 'auto-stretch'; // 自动拉伸模式：根据内容自动调整，最大不超过editor_max_height
      return 'adaptive'; // 自适应模式：容器100%高度减去底部工具栏高度
    })();

    const { t } = useTranslation();
    const isAutoHeight = heightMode === 'adaptive';
    // const isAutoStretch = heightMode === 'auto-stretch';
    const isFixedHeight = heightMode === 'fixed';

    // 调试日志
    console.log('LxEditor Height Mode:', {
      heightMode,
      init_height: init.height,
      height,
      editor_max_height: init.editor_max_height,
      isFixedHeight,
      // isAutoStretch,
      isAutoHeight,
    });

    // 监听底部容器高度变化（自适应模式和自动拉伸模式都需要）
    const updateBottomContainerHeight = useCallback(() => {
      if (bottomContainerRef.current && isAutoHeight) {
        const rect = bottomContainerRef.current.getBoundingClientRect();
        const newHeight = rect.height;
        console.log('updateBottomContainerHeight', newHeight, bottomContainerHeight);
        if (newHeight !== bottomContainerHeight) {
          setBottomContainerHeight(newHeight);
        }
      }
    }, [bottomContainerHeight, isAutoHeight]);
    
    // 使用封装的拖拽 Hook
    // 使用 dragCaptureRef 而不是 containerRef，扩大拖拽捕获区域
    const { state: dragState, handlers: dragHandlers } = useEditorDragDropWithListeners(
      {
        enableDraggingImageUpload,
        enableDraggingAttachmentUpload,
        containerRef, // 使用扩大的捕获容器
      },
      {
        onImageDrop: useCallback((files: File[]) => {
          if (editorInstance && enableDraggingImageUpload) {
            files.forEach(file => {
              const mockEvent = {
                target: {
                  files: [file],
                  dom: {
                    files: [file],
                  },
                },
              };
              editorInstance.execCommand('lxFileChangeUploadImage', false, mockEvent);
            });
          }
        }, [editorInstance, enableDraggingImageUpload]),
        onAttachmentDrop: useCallback((files: File[]) => {
          if (enableDraggingAttachmentUpload && init.attachment_upload_handler) {
            init.attachment_upload_handler(files);
          }
        }, [enableDraggingAttachmentUpload, init.attachment_upload_handler]),
      }
    );
    
    const { dragging, draggingAllImg } = dragState;
    const { handleDrop } = dragHandlers;
    
    // 使用ResizeObserver监听底部容器高度变化（自适应和自动拉伸模式下）
    useEffect(() => {
      if (!bottomContainerRef.current || !isAutoHeight) return;

      const resizeObserver = new ResizeObserver(() => {
        updateBottomContainerHeight();
      });

      resizeObserver.observe(bottomContainerRef.current);

      // 初始化时也计算一次
      updateBottomContainerHeight();

      return () => {
        resizeObserver.disconnect();
      };
    }, [updateBottomContainerHeight, isAutoHeight]);

    // 同步 ref 和 state
    // useEffect(() => {
    //   showImageLayerRef.current = showImageLayer;
    // }, [showImageLayer]);

    // useEffect(() => {
    //   showAttachmentLayerRef.current = showAttachmentLayer;
    // }, [showAttachmentLayer]);
    // 获取编辑器内容高度（抽离出来的公共逻辑）
    const getEditorContentHeight = useCallback(() => {
      if (!editorInstance) return 0;

      // 获取编辑器的body元素（iframe内的mce-content-body）
      const editorBody = editorInstance.getBody();
      if (!editorBody) return 0;

      // 获取iframe内容的实际高度
      const contentHeight = editorBody.scrollHeight;
      return contentHeight;
    }, [editorInstance]);

    // 获取iframe内容高度的逻辑（仅在自动拉伸模式下使用）
    const adjustEditorHeight = useCallback(() => {
      if (!editorInstance || !containerRef.current || !init.editor_max_height) {
        return;
      }

      // 使用抽离出来的公共逻辑获取内容高度
      const contentHeight = getEditorContentHeight();
      if (!contentHeight) return;

      // 使用动态的底部容器高度
      const toolbarHeight = bottomContainerHeight;
      const minHeight = init.min_height || 100;

      // 计算需要的内容高度，至少为最小高度
      const targetContentHeight = Math.max(contentHeight, minHeight);
      const totalHeight = targetContentHeight + toolbarHeight;

      console.log('=== Auto Stretch Mode ===');
      console.log('heightMode:', heightMode);
      console.log('contentHeight (iframe内容高度):', contentHeight);
      console.log('targetContentHeight:', targetContentHeight);
      console.log('totalHeight:', totalHeight);
      console.log('bottomContainerHeight:', bottomContainerHeight);
      console.log('init.editor_max_height:', init.editor_max_height);

      // 获取TinyMCE容器元素
      const tinymceContainer = containerRef.current.querySelector('.tox.tox-tinymce') as HTMLElement;
      const scrollBox = containerRef.current.querySelector('.scrollBox') as HTMLElement;

      // 如果总高度小于最大高度，则调整容器高度
      if (totalHeight <= init.editor_max_height) {
        containerRef.current.style.height = `${totalHeight}px`;

        // 调整scrollBox的高度，去掉工具栏高度
        if (scrollBox) {
          scrollBox.style.height = `${targetContentHeight}px`;
        }

        // 调整TinyMCE容器的高度
        if (tinymceContainer) {
          tinymceContainer.style.height = `${targetContentHeight}px`;
        }

        // 调整编辑器容器高度
        requestAnimationFrame(() => {
          editorInstance.getContainer().style.height = `${targetContentHeight}px`;
        });
      } else {
        const maxContentHeight = init.editor_max_height - toolbarHeight;
        containerRef.current.style.height = `${init.editor_max_height}px`;

        // 当达到最大高度时，scrollBox高度为最大高度减去工具栏高度
        if (scrollBox) {
          scrollBox.style.height = `${maxContentHeight}px`;
        }

        // 调整TinyMCE容器的高度
        if (tinymceContainer) {
          tinymceContainer.style.height = `${maxContentHeight}px`;
        }

        // 调整编辑器容器高度
        requestAnimationFrame(() => {
          editorInstance.getContainer().style.height = `${maxContentHeight}px`;
        });
      }
    }, [editorInstance, bottomContainerHeight, getEditorContentHeight, init.editor_max_height, init.min_height]);

    // 监听编辑器内容变化（仅在自动拉伸模式下）
    useEffect(() => {
      if (!editorInstance || !init.editor_max_height) return;

      const handleContentChange = () => {
        // 使用setTimeout确保DOM更新完成后再计算高度
        setTimeout(adjustEditorHeight, 0);
      };

      // 监听多种可能导致内容高度变化的事件
      editorInstance.on('input', handleContentChange);
      editorInstance.on('keyup', handleContentChange);
      editorInstance.on('paste', handleContentChange);
      editorInstance.on('NodeChange', handleContentChange);
      editorInstance.on('SetContent', handleContentChange);
      editorInstance.on('change', handleContentChange);

      return () => {
        editorInstance.off('input', handleContentChange);
        editorInstance.off('keyup', handleContentChange);
        editorInstance.off('paste', handleContentChange);
        editorInstance.off('NodeChange', handleContentChange);
        editorInstance.off('SetContent', handleContentChange);
        editorInstance.off('change', handleContentChange);
      };
    }, [editorInstance, adjustEditorHeight, init.editor_max_height]);

    // 监听底部容器高度变化，重新调整编辑器高度（仅在自动拉伸模式下）
    useEffect(() => {
      if (editorInstance && init.editor_max_height) {
        console.log('adjustEditorHeight', bottomContainerHeight);
        // setTimeout(adjustEditorHeight, 0);
        adjustEditorHeight();
      }
    }, [bottomContainerHeight, editorInstance, adjustEditorHeight]);

    // 监听编辑器内容高度变化并通知父组件（仅当开启了内容高度跟踪时）
    useEffect(() => {
      if (!editorInstance || !enableContentHeightTracking || !onContentHeightChange) return;

      const handleContentChange = () => {
        // 使用抽离出来的公共逻辑获取内容高度
        const contentHeight = getEditorContentHeight();
        if (contentHeight > 0) {
          const totalHeight = contentHeight + bottomContainerHeight;

          if (totalHeight !== editorContentHeight) {
            console.log('Content height changed:', contentHeight, 'total height:', totalHeight);
            setEditorContentHeight(totalHeight);
            onContentHeightChange(totalHeight);
          }
        }
      };

      // 监听多种可能导致内容高度变化的事件
      editorInstance.on('input', handleContentChange);
      editorInstance.on('keyup', handleContentChange);
      editorInstance.on('paste', handleContentChange);
      editorInstance.on('NodeChange', handleContentChange);
      editorInstance.on('SetContent', handleContentChange);
      editorInstance.on('change', handleContentChange);

      // 初始化时也计算一次
      setTimeout(handleContentChange, 100);

      return () => {
        editorInstance.off('input', handleContentChange);
        editorInstance.off('keyup', handleContentChange);
        editorInstance.off('paste', handleContentChange);
        editorInstance.off('NodeChange', handleContentChange);
        editorInstance.off('SetContent', handleContentChange);
        editorInstance.off('change', handleContentChange);
      };
    }, [
      editorInstance,
      enableContentHeightTracking,
      onContentHeightChange,
      getEditorContentHeight,
      bottomContainerHeight,
    ]);

    // 清理链接对话框处理器
    useEffect(() => {
      return () => {
        if (linkDialogHandler) {
          linkDialogHandler.destroy();
        }
      };
    }, [linkDialogHandler]);

    // 合并setup函数
    const mergedSetup = (editor: EditorType) => {
      // 执行传入的setup
      if (init.setup) {
        init.setup(editor);
      }
    };

    const config = {
      tinymceScriptSrc: `/pub_assets/tinymce/tinymce.js?nub=${Math.random}`,
      id: editorId, // 为编辑器实例设置唯一 ID
      init: {
        ...editorConfig,
        ...init,
        setup: mergedSetup, // 使用合并后的setup函数
        isWeb: true,
        body_class: 'mce-content-body-white',
        body_id: `${editorId}-body`, // 为 iframe body 设置唯一 ID，进一步隔离编辑器实例
        grammarAction,
        previewAction,
        productImgAction,
        insertEmojiAction,
        aiWriteMailAction,
        mouseoverToolbar,
        mouseoutToolbar,
        siriusVersion,
        captureScreenAction,
        AIWriteContentionShowAction,
        utilsAction,
        colorAction,
        fontSizeAction,
        lineheightAction,
        fontfamilyAction,
        // track: trackApi,
        init_instance_callback: (editor: EditorType) => {
          seteditorInstance(editor);
          eventsOut(editor);

          // 创建链接对话框处理器
          const handler = createLinkDialogHandler(editor, openLinkDialog);
          setLinkDialogHandler(handler);

          onEditCreated && onEditCreated(editor);
          init.init_instance_callback && init.init_instance_callback(editor);

          // 拖拽事件已在外层容器处理，不再在编辑器实例上处理

          // 设置编辑器背景为白色
          const editorBody = editor.getBody();
          if (editorBody) {
            editorBody.style.backgroundColor = '#ffffff';
          }

          // 编辑器初始化后立即调整高度（仅在自动拉伸模式下）
          if (init.editor_max_height) {
            setTimeout(adjustEditorHeight, 100);
          }
        },
        toolbar: orgToolbar,
        automatic_uploads: false,
      },
      ...rest,
    };
    return (
      <div
        ref={containerRef}
        className={classnames(uniqueClass, 'relative h-full')}
      >
        {/* 四个隐藏的拖拽捕获层 - 分别位于编辑器四周，用于捕获从外部拖入的事件 */}
        {/* 这些层完全透明，只覆盖编辑器边缘区域，不影响编辑器内部的正常编辑 */}
        {(enableDraggingImageUpload || enableDraggingAttachmentUpload) && (
          <div
            // ref={dragCaptureRef}
            className="absolute"
            style={{
              // 包装容器，覆盖整个编辑器区域并向外扩展 20px
              // pointer-events: none 让中心区域点击穿透，子元素边缘捕获层单独设置 pointer-events: auto 保持拖拽捕获
              top: '-20px',
              left: '-20px',
              right: '-20px',
              bottom: '-20px',
              pointerEvents: 'none',
            }}
          >
            {/* 上边缘捕获层 */}
            <div
              className="absolute z-30"
              style={{
                backgroundColor: 'transparent',
                top: '16px',
                left: '0',
                right: '0',
                height: '20px',
                pointerEvents: 'auto',
              }}
            />
            {/* 下边缘捕获层 */}
            <div
              className="absolute z-30"
              style={{
                backgroundColor: 'transparent',
                bottom: '0',
                left: '0',
                right: '0',
                height: '20px',
                pointerEvents: 'auto',
              }}
            />
            {/* 左边缘捕获层 */}
            <div
              className="absolute z-30"
              style={{
                backgroundColor: 'transparent',
                top: '20px',
                left: '16px',
                bottom: '20px',
                width: '20px',
                pointerEvents: 'auto',
              }}
            />
            {/* 右边缘捕获层 */}
            <div
              className="absolute z-30"
              style={{
                backgroundColor: 'transparent',
                top: '20px',
                right: '16px',
                bottom: '20px',
                width: '20px',
                pointerEvents: 'auto',
              }}
            />
          </div>
        )}
        <div
          className={classnames('overflow-auto bg-white', 'scrollBox')}
          // ref={containerContentRef}
          style={{
            height: `calc(100% - ${bottomContainerHeight}px)`,
            borderRadius: '8px 8px 0 0',
            // pointerEvents: dragging ? 'none' : 'auto',
          }}
        >
          <Editor {...config} />
          {showQuotedDot && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className={'pl-4 cursor-pointer relative'} onClick={() => quotedContentAction && quotedContentAction()}>
                    <img width="28px" height="14px" src={quotedDot} alt="quoted-dot" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">{t('Display the hidden email content')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {init.attachedStickyBottom ? null : children}
          {/* {toolbarAlways.includes('aiWriter') && editorInstance && <AICard editorInstance={editorInstance} />} */}
        </div>

        {/* 底部容器，包含所有底部组件（Theme工具栏、AI输入框等）
            通过ResizeObserver监听此容器高度变化，动态调整编辑器内容区域高度 */}
        <div ref={bottomContainerRef} className="bottom-container">
          {/* {editorInstance && toolbarAlways.includes('aiWriter') && (
            <AITextInput height={height} editorInstance={editorInstance} />
          )} */}
          {init.attachedStickyBottom ? children : null}
          {editorInstance && (
            <Theme
              toolbarAlways={toolbarAlways}
              toolbarExtendA={toolbarExtendA}
              toolbarExtend={toolbarExtend}
              toolbarAutoExtend={toolbarAutoExtend}
              toolbarAutoExtendRow2={toolbarAutoExtendRow2}
              editorInstance={editorInstance}
              height={height}
              customToolbarPlugin={customToolbarPlugin}
              {...themeParam}
            />
          )}
          {/* 这里可以添加其他底部组件 */}
        </div>
        {/* 拖拽上传蒙层容器 - 参考 WritePage.tsx 的实现，在外层容器捕获拖拽后显示 */}
        {(enableDraggingImageUpload || enableDraggingAttachmentUpload) && dragging && (
          <div className="pointer-events-none absolute inset-0 z-50">
            {/* 图片上传蒙层 - 当全部是图片时显示 */}
            {enableDraggingImageUpload && draggingAllImg && (
              <div
                className="absolute m-2 rounded top-0 flex items-center justify-center bg-[#0000000F] text-gray-700 border border-dashed border-[#D4D4D4] transition-opacity pointer-events-auto"
                style={{
                  height: isFixedHeight ? `calc(${init.height || height || 200}px - 16px)` : `calc(100% - ${bottomContainerHeight}px - 16px)`,
                  width:
                    enableDraggingAttachmentUpload && enableDraggingImageUpload
                      ? `calc(50% - 18px)`
                      : `calc(100% - 32px)`,
                  left: '8px',
                }}
                onDragEnter={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDragLeave={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e: React.DragEvent) => {
                  handleDrop(e, 'img');
                }}
              >
                <div className="text-center">
                  <div className="text-sm font-medium">
                    Insert at the <strong>cursor</strong> position in the main text.
                  </div>
                </div>
              </div>
            )}

            {/* 附件上传蒙层 - 当启用附件上传时显示 */}
            {enableDraggingAttachmentUpload && (
              <div
                className="absolute m-2 rounded top-0 flex items-center justify-center bg-[#0000000F] text-gray-700 border border-dashed border-[#D4D4D4] transition-opacity pointer-events-auto"
                style={{
                  height: isFixedHeight ? `calc(${init.height || height || 200}px - 16px)` : `calc(100% - ${bottomContainerHeight}px - 16px)`,
                  width:
                    enableDraggingAttachmentUpload && enableDraggingImageUpload && draggingAllImg
                      ? `calc(50% - 18px)`
                      : `calc(100% - 16px)`,
                  right: enableDraggingAttachmentUpload && enableDraggingImageUpload && draggingAllImg ? '8px' : 'auto',
                  left: enableDraggingAttachmentUpload && enableDraggingImageUpload && draggingAllImg ? 'auto' : '8px',
                }}
                onDragEnter={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDragLeave={(e: React.DragEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e: React.DragEvent) => {
                  handleDrop(e, 'attachment');
                }}
              >
                <div className="text-center">
                  <div className="text-sm font-medium">
                    Upload <strong>attachments</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* <div className="pointer-events-none absolute m-2 w-[calc(100%-16px)] h-[calc(100%-48px-16px)] border border-dashed border-gray-400 rounded top-0 flex items-center justify-center bg-black/10 text-gray-700">Add As Image</div> */}
      </div>
    );
  }
);

// 外层组件，提供LinkManager上下文
const LxEditor = React.forwardRef<any, Props>((props, ref) => {
  return (
    <LinkManagerProvider>
      <LxEditorInner {...props} ref={ref}>
        {props.children}
      </LxEditorInner>
    </LinkManagerProvider>
  );
});

export default LxEditor;
